import React from 'react';
import { useTable, useSortBy, usePagination, useGlobalFilter } from 'react-table';
import { FaSort, FaSortUp, FaSortDown } from 'react-icons/fa';

function DataTableStructure({ 
    columns, 
    data, 
    initialPageSize = 5,
    globalFilter,
    setGlobalFilter,
    initialSortBy,
    onSortChange
}) {
    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        page,
        prepareRow,
        setGlobalFilter: setTableFilter,
        nextPage,
        previousPage,
        canNextPage,
        canPreviousPage,
        pageOptions,
        setPageSize,
        state: { pageIndex, pageSize, sortBy },
    } = useTable(
        {
            columns,
            data,
            initialState: { 
                pageSize: initialPageSize,
                sortBy: initialSortBy
            },
            disableMultiSort: true,
            disableSortRemove: true,
        },
        useGlobalFilter,
        useSortBy,
        usePagination
    );

    // Handle search input changes
    const handleSearchChange = (value) => {
        setTableFilter(value);
        setGlobalFilter(value);
    };

    // Sync external filter with table filter
    React.useEffect(() => {
        if (globalFilter !== undefined) {
            setTableFilter(globalFilter);
        }
    }, [globalFilter, setTableFilter]);

    // Add effect to sync sort state
    React.useEffect(() => {
        if (onSortChange) {
            onSortChange(sortBy);
        }
    }, [sortBy, onSortChange]);

    return (
        <div className="flex flex-col">
            <div className="flex justify-between items-center mb-4">
                <div className="flex items-center gap-4">
                    <div className="relative">
                        <input
                            type="text"
                            placeholder="Search..."
                            className="px-4 py-2 border-2 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 w-40"
                            value={globalFilter || ''}
                            onChange={e => handleSearchChange(e.target.value)}
                        />
                    </div>
                    <select
                        value={pageSize}
                        onChange={e => setPageSize(Number(e.target.value))}
                        className="mt-0.5 px-4 py-2 border-2 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                        {[5, 10, 20, 50].map(size => (
                            <option key={size} value={size}>
                                Show {size}
                            </option>
                        ))}
                    </select>
                </div>
            </div>

            <div className="bg-white shadow rounded-lg overflow-hidden">
                {data.length === 0 ? (
                    <p className="p-4 text-gray-500">No data available</p>
                ) : (
                    <>
                        <div className="overflow-x-auto">
                            <table {...getTableProps()} className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-violet-200 dark:bg-teal-800 select-none">
                                    {headerGroups.map(headerGroup => {
                                        const { key, ...headerGroupProps } = headerGroup.getHeaderGroupProps();
                                        return (
                                            <tr key={key} {...headerGroupProps}>
                                                {headerGroup.headers.map(column => {
                                                    const { key, ...columnProps } = column.getHeaderProps(column.getSortByToggleProps());
                                                    return (
                                                        <th
                                                            key={key}
                                                            {...columnProps}
                                                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                                                        >
                                                            <div className="flex items-center gap-2">
                                                                {column.render('Header')}
                                                                {column.canSort && (
                                                                    <span>
                                                                        {column.isSorted
                                                                            ? column.isSortedDesc
                                                                                ? <FaSortDown />
                                                                                : <FaSortUp />
                                                                            : <FaSort />}
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </th>
                                                    );
                                                })}
                                            </tr>
                                        );
                                    })}
                                </thead>
                                <tbody {...getTableBodyProps()} className="divide-y divide-gray-200">
                                    {page.map(row => {
                                        prepareRow(row);
                                        const { key, ...rowProps } = row.getRowProps();
                                        return (
                                            <tr key={key} {...rowProps} className="hover:bg-gray-100 transition-colors duration-200">
                                                {row.cells.map(cell => {
                                                    const { key, ...cellProps } = cell.getCellProps();
                                                    return (
                                                        <td
                                                            key={key}
                                                            {...cellProps}
                                                            className="px-6 py-4 whitespace-nowrap text-gray-900"
                                                        >
                                                            {cell.render('Cell')}
                                                        </td>
                                                    );
                                                })}
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                        <div className="px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                            <div className="flex-1 flex justify-between sm:hidden">
                                <button
                                    onClick={() => previousPage()}
                                    disabled={!canPreviousPage}
                                    className="relative inline-flex items-center px-4 py-2 border text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                                >
                                    Previous
                                </button>
                                <button
                                    onClick={() => nextPage()}
                                    disabled={!canNextPage}
                                    className="ml-3 relative inline-flex items-center px-4 py-2 border text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                                >
                                    Next
                                </button>
                            </div>
                            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                                <div>
                                    <p className="text-sm text-gray-700">
                                        Page <span className="font-medium">{pageIndex + 1}</span> of{' '}
                                        <span className="font-medium">{pageOptions.length}</span>
                                    </p>
                                </div>
                                <div>
                                    <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                                        <button
                                            onClick={() => previousPage()}
                                            disabled={!canPreviousPage}
                                            className="relative inline-flex items-center px-2 py-2 rounded-l-md border text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 dark:hover:text-gray-900"
                                        >
                                            Previous
                                        </button>
                                        <button
                                            onClick={() => nextPage()}
                                            disabled={!canNextPage}
                                            className="relative inline-flex items-center px-2 py-2 rounded-r-md border text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 dark:hover:text-gray-900"
                                        >
                                            Next
                                        </button>
                                    </nav>
                                </div>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

export default DataTableStructure;